/**
 * The four-eyes inbox (W7-CASH-HANDOVER-CONSOLE, D411; Admin checklist #61) — one page listing every queue where a money
 * or policy change waits for a SECOND person, each line opening the screen where it is decided.
 *
 * It is a CLIENT aggregation of reads the console already makes on screens of their own (plus, since the D420 review, the
 * platform-wide read of the pending pack retirements):
 *
 *   settings_changes   `GET /v1/config/settings`                  (`config:view`) — pending changes + rollbacks awaiting review
 *   payouts            `GET /v1/payouts/queue?market=&status=REQUESTED` (`payout:view`, per market)
 *   cash_handovers     `GET /v1/cash-handovers?market=&status=RECORDED` (`payout:view`, per market)
 *   refund_approvals   `GET /v1/refund-approvals?status=PENDING`  (`refund:view`, all markets)
 *   service_areas     `GET /v1/service-areas?market=`            (`service_area:view`, per market: the SUBMITTED proposal, 0 or 1)
 *   rider_wallet      `GET /v1/ops/rider-wallet/adjustments?market=&status=PENDING_APPROVAL` (`rider_wallet:approve`, per market; D737b)
 *   country_packs      `GET /v1/config/country-packs/:cc`         (`config:view`, one read per country with a market: the DRAFTs)
 *                      `GET /v1/config/country-packs/retirements/pending` (`config:view`, platform-wide: the pending
 *                      retirements — D420 review W7G-01, a retirement's country usually has no market to walk to)
 *
 * Fail closed per source (the W6N-14 lesson): a queue that was refused or failed is **unavailable** — never a zero — and
 * the grand total is only offered as complete when every line answered. A per-market queue with no market chosen was
 * not asked and says so (W6N-11). A queue the operator's grants cannot read is left out entirely and never asked for. A
 * read that returned its limit may have been cut, and says so ("200+"). Two four-eyes surfaces have no pending read the
 * inbox could aggregate and are named instead of silently left out: pricing rules (read per market AND category) and
 * merchant promotions (read per merchant).
 *
 * Who may decide what mirrors the server's rule, for display only: never one's own item, never a payout to oneself or a
 * handover of one's own cash; the refund queue answers it itself (`approvableByMe`). Settings changes are decided per
 * bucket scope on the server, so the inbox does not guess how many of them this operator may decide.
 *
 * D868: a GLOBAL super administrator with no access profile (`maySelfApprove`) may decide their OWN items, so those count
 * as decidable for them (they still count as `own`). The subject rules stay for everyone: a payout to oneself, one's own
 * cash as the courier, an adjustment of one's own rider account are never decidable.
 */
import {
  type ApiError,
  type CashHandoverApi,
  type CashHandoverView,
  type ConfigApi,
  type CountryPackView,
  type CountryPackRetirementView,
  type FinanceApi,
  type MarketsApi,
  type OpsApi,
  type GovernedSettingSummaryView,
  type OperatingMarketView,
  type PayoutRow,
  type RefundApprovalView,
  type RefundApprovalsApi,
  type RiderWalletAdminApi,
  type RiderWalletAdjustmentView,
  type SettingsApi,
} from '@vello/api-client';
import { type Result } from '@vello/core';
import { grantsAllow, type KnownGrants, type ScreenPolicy } from './console-access.js';
import { maySelfApprove } from './self-approval.js';

export type FourEyesSourceKey = 'settings_changes' | 'payouts' | 'cash_handovers' | 'refund_approvals' | 'country_packs' | 'service_areas' | 'rider_wallet';

export const FOUR_EYES_SOURCE_KEYS: readonly FourEyesSourceKey[] = ['settings_changes', 'payouts', 'cash_handovers', 'refund_approvals', 'country_packs', 'service_areas', 'rider_wallet'];

/** The screen each line opens, the word + target its read needs (mirrors `SCREEN_POLICIES`), and whether it is per market. */
export const FOUR_EYES_SOURCES: Readonly<Record<FourEyesSourceKey, { readonly route: string; readonly policy: ScreenPolicy; readonly perMarket: boolean }>> = {
  settings_changes: { route: 'governed_settings', policy: { permission: 'config:view', target: 'PLATFORM' }, perMarket: false },
  payouts: { route: 'finance_payouts', policy: { permission: 'payout:view', target: 'PLATFORM' }, perMarket: true },
  cash_handovers: { route: 'finance_cash_handovers', policy: { permission: 'payout:view', target: 'PLATFORM' }, perMarket: true },
  refund_approvals: { route: 'finance_refund_approvals', policy: { permission: 'refund:view', target: 'PLATFORM' }, perMarket: false },
  country_packs: { route: 'registries', policy: { permission: 'config:view', target: 'PLATFORM' }, perMarket: false },
  // D616 round 2: the market's one open service-area proposal, once SUBMITTED, waits for a second manager (ADR-0121).
  service_areas: { route: 'service_areas', policy: { permission: 'service_area:view', target: 'GEO' }, perMarket: true },
  // D737b (ADR-0150): the market's rider-balance adjustments waiting for a second operator.
  rider_wallet: { route: 'finance_rider_wallet', policy: { permission: 'rider_wallet:approve', target: 'GEO' }, perMarket: true },
};

/** The four-eyes surfaces the inbox cannot aggregate (no pending read across their subjects) — named on the page. */
export const FOUR_EYES_NOT_AGGREGATED = ['pricing_rules', 'merchant_promotions'] as const;

/**
 * A market's service-area proposal waiting for approval: at most one is open per market, so the count is 0 or 1. Only a SUBMITTED
 * proposal waits for a second manager (a DRAFT is still its author's). `decidable` needs `service_area:manage` AND a different author
 * (or, D868, a super administrator deciding their own: `selfApproval`).
 */
export function countSubmittedServiceArea(open: { readonly status: string; readonly proposedBy: string } | null | undefined, operatorId: string | undefined, mayManage: boolean, selfApproval = false): FourEyesCount {
  const waiting = open !== null && open !== undefined && open.status === 'SUBMITTED' ? open : undefined;
  if (waiting === undefined) return { total: 0, truncated: false, decidable: 0, own: 0 };
  const own = mine(waiting.proposedBy, operatorId) ? 1 : 0;
  return { total: 1, truncated: false, decidable: mayManage ? (selfApproval ? 1 : 1 - own) : 0, own };
}

/**
 * A market's rider-balance adjustments waiting for approval (D737b): every PENDING_APPROVAL one. `decidable` = not proposed by me, not about my own
 * rider account, and I hold `rider_wallet:approve`; `own` = the ones I proposed (they wait for someone else - unless, D868, I am a
 * super administrator: `selfApproval` makes my own decidable, never one about my own account).
 */
export function countPendingRiderWallet(rows: readonly Pick<RiderWalletAdjustmentView, 'status' | 'proposedBy' | 'riderId'>[], operatorId: string | undefined, mayApprove: boolean, limit: number, selfApproval = false): FourEyesCount {
  const pending = rows.filter((a) => a.status === 'PENDING_APPROVAL');
  const decidable = pending.filter((a) => (selfApproval || !mine(a.proposedBy, operatorId)) && !mine(a.riderId, operatorId)).length;
  return { total: pending.length, truncated: rows.length >= limit, decidable: mayApprove ? decidable : 0, own: pending.filter((a) => mine(a.proposedBy, operatorId)).length };
}

/** The largest page each queue read asks for (the servers' caps). A read that returns this many may have been cut. */
export const FOUR_EYES_READ_LIMIT = 200;

/**
 * What one queue holds, as far as its read could tell: `total` waiting, `truncated` when the read hit its limit,
 * `decidable` = how many THIS operator may decide (absent when the inbox cannot know), `own` = how many it made itself
 * (they wait for someone else; absent when unknown).
 */
export interface FourEyesCount {
  readonly total: number;
  readonly truncated: boolean;
  readonly decidable?: number;
  readonly own?: number;
}

/**
 * A read's outcome: a count, `undefined` while loading, `null` when refused/failed, `'needs_market'` when not asked, `'absent'` when
 * the runtime carries no client for the source (D616 round 3: a caller built before the source existed — the line is left out, the
 * inbox never breaks).
 */
export type FourEyesRead = FourEyesCount | undefined | null | 'needs_market' | 'absent';
export type FourEyesCounts = Readonly<Record<FourEyesSourceKey, FourEyesRead>>;

export interface FourEyesLine {
  readonly key: FourEyesSourceKey;
  readonly route: string;
  readonly perMarket: boolean;
  readonly state: 'loading' | 'ready' | 'unavailable' | 'needs_market';
  /** Present only when `state === 'ready'` — an unread queue has no number, not a zero. */
  readonly count?: FourEyesCount;
}

const sameId = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();
const mine = (id: string | null | undefined, operatorId: string | undefined): boolean => operatorId !== undefined && id !== null && id !== undefined && id !== '' && sameId(id, operatorId);

/** Whether the inbox may ask for a queue at all: never for one the grants cannot read. Unknown authority asks. */
export function fourEyesReadAllowed(key: FourEyesSourceKey, grants: KnownGrants): boolean {
  return grants === undefined || grantsAllow(grants, FOUR_EYES_SOURCES[key].policy);
}

/** Payouts awaiting approval: decidable = attributed, not issued by me (unless `selfApproval`, D868), never to me, and I hold `payout:approve`. */
export function countPendingPayouts(rows: readonly PayoutRow[], operatorId: string | undefined, mayApprove: boolean, limit: number, selfApproval = false): FourEyesCount {
  const pending = rows.filter((p) => p.status === 'REQUESTED');
  const decidable = pending.filter((p) => p.requestedBy !== null && p.requestedBy !== undefined && p.requestedBy !== '' && (selfApproval || !mine(p.requestedBy, operatorId)) && !mine(p.driverId, operatorId)).length;
  return { total: pending.length, truncated: rows.length >= limit, decidable: mayApprove ? decidable : 0, own: pending.filter((p) => mine(p.requestedBy, operatorId)).length };
}

/** Cash handovers awaiting confirmation: decidable = not recorded by me (unless `selfApproval`, D868), never my own cash, and I hold `payout:settle`. */
export function countRecordedHandovers(rows: readonly CashHandoverView[], operatorId: string | undefined, mayConfirm: boolean, limit: number, selfApproval = false): FourEyesCount {
  const pending = rows.filter((h) => h.status === 'RECORDED');
  const decidable = pending.filter((h) => (selfApproval || !mine(h.recordedBy, operatorId)) && !mine(h.courierId, operatorId)).length;
  return { total: pending.length, truncated: rows.length >= limit, decidable: mayConfirm ? decidable : 0, own: pending.filter((h) => mine(h.recordedBy, operatorId)).length };
}

/** Refunds and credits awaiting approval: the server says which ones this viewer may approve (`approvableByMe`). */
export function countPendingRefunds(rows: readonly Pick<RefundApprovalView, 'status' | 'requestedBy' | 'approvableByMe'>[], operatorId: string | undefined, limit: number): FourEyesCount {
  const pending = rows.filter((r) => r.status === 'PENDING');
  return { total: pending.length, truncated: rows.length >= limit, decidable: pending.filter((r) => r.approvableByMe).length, own: pending.filter((r) => mine(r.requestedBy, operatorId)).length };
}

/**
 * Governed settings: every change pending review plus every applied one-step rollback still awaiting its post-review
 * (review W6G-01). The registry read is whole (no limit). Mine = the pending changes I proposed.
 */
export function countPendingSettings(settings: readonly GovernedSettingSummaryView[], operatorId?: string): FourEyesCount {
  let total = 0;
  let own = 0;
  for (const s of settings) {
    total += (s.pendingCount ?? 0) + (s.awaitingReviewCount ?? 0);
    for (const b of s.buckets ?? []) own += (b.pending ?? []).filter((c) => c.status === 'PENDING_REVIEW' && mine(c.requestedBy, operatorId)).length;
  }
  return { total, truncated: false, own };
}

/**
 * Country packs awaiting approval (DRAFT versions), over one read per country. One country that could not be read
 * makes the whole line unavailable (`null`) — a partial count would read as a complete one. D420: a PENDING retirement
 * (an APPROVED version a maker proposed to retire) waits for a second operator too, so it counts on the same line; the
 * proposer's own is `own`, and `decidable` only for a super administrator (`selfApproval`, D868).
 */
export function countDraftPacks(
  perCountry: readonly (readonly CountryPackView[] | null)[],
  operatorId: string | undefined,
  mayApprove: boolean,
  retirements: readonly Pick<CountryPackRetirementView, 'state' | 'proposedBy'>[] = [],
  selfApproval = false,
): FourEyesCount | null {
  if (perCountry.some((packs) => packs === null)) return null;
  const drafts = perCountry.flatMap((packs) => (packs ?? []).filter((p) => p.status === 'DRAFT'));
  const pendingRetire = retirements.filter((r) => r.state === 'PENDING');
  const own = drafts.filter((p) => mine(p.audit?.createdBy, operatorId)).length + pendingRetire.filter((r) => mine(r.proposedBy, operatorId)).length;
  const total = drafts.length + pendingRetire.length;
  return { total, truncated: false, decidable: mayApprove ? (selfApproval ? total : total - own) : 0, own };
}

/** The countries whose packs the inbox reads: those with a registered market, upper-cased, sorted, once each. */
export function fourEyesCountries(markets: readonly Pick<OperatingMarketView, 'countryCode'>[]): readonly string[] {
  return [...new Set(markets.map((m) => m.countryCode.toUpperCase()).filter((c) => /^[A-Z]{2}$/.test(c)))].sort();
}

/**
 * The inbox's lines, in the order an operator reads them: the queues with something waiting first (biggest first), then
 * the quiet ones, then loading, then "choose a market", then the ones that could not be read. A queue this operator's
 * grants cannot read is left out entirely.
 */
export function buildFourEyesInbox(counts: FourEyesCounts, grants: KnownGrants): readonly FourEyesLine[] {
  const lines: { line: FourEyesLine; index: number; rank: number }[] = [];
  FOUR_EYES_SOURCE_KEYS.forEach((key, index) => {
    if (!fourEyesReadAllowed(key, grants)) return;
    const source = FOUR_EYES_SOURCES[key];
    const value = counts[key];
    if (value === 'absent') return;
    const base = { key, route: source.route, perMarket: source.perMarket };
    const line: FourEyesLine =
      value === undefined ? { ...base, state: 'loading' }
        : value === null ? { ...base, state: 'unavailable' }
          : value === 'needs_market' ? { ...base, state: 'needs_market' }
            : { ...base, state: 'ready', count: value };
    const rank = line.state === 'ready' ? ((line.count?.total ?? 0) > 0 ? 0 : 1) : line.state === 'loading' ? 2 : line.state === 'needs_market' ? 3 : 4;
    lines.push({ line, index, rank });
  });
  return lines
    .sort((a, b) => a.rank - b.rank || (a.rank === 0 ? (b.line.count?.total ?? 0) - (a.line.count?.total ?? 0) : 0) || a.index - b.index)
    .map((e) => e.line);
}

/** The grand total: `complete` only when every line answered — otherwise the page says the total is incomplete. */
export function fourEyesTotal(lines: readonly FourEyesLine[]): { readonly complete: boolean; readonly total: number; readonly truncated: boolean } {
  let total = 0;
  let truncated = false;
  for (const l of lines) {
    if (l.state !== 'ready' || l.count === undefined) continue;
    total += l.count.total;
    truncated = truncated || l.count.truncated;
  }
  return { complete: lines.every((l) => l.state === 'ready'), total, truncated };
}

/** The endpoint groups the inbox reads (the Admin runtime satisfies it structurally). */
export interface FourEyesInboxRuntime {
  readonly settings: Pick<SettingsApi, 'list'>;
  readonly finance: Pick<FinanceApi, 'payoutQueue'>;
  readonly cashHandovers: Pick<CashHandoverApi, 'queue'>;
  readonly refundApprovals: Pick<RefundApprovalsApi, 'list'>;
  readonly config: Pick<ConfigApi, 'listCountryPacks' | 'listPendingCountryPackRetirements'>;
  /** D616 round 2: the market's service-area proposal (`serviceAreas(market)`); optional — without it the line is left out. */
  readonly ops?: Partial<Pick<OpsApi, 'serviceAreas'>>;
  /** D737b: the market's rider-balance approvals (`riderWallet.inbox(market)`); optional - without it the line is left out. */
  readonly riderWallet?: Partial<Pick<RiderWalletAdminApi, 'inbox'>>;
  /**
   * The market catalog that names the countries whose packs are read. Read HERE, not taken from the console session:
   * the session turns a failed catalog read into `[]`, which would count zero DRAFT packs as a real zero (review W7H-01).
   */
  readonly markets: Pick<MarketsApi, 'listMarkets'>;
}

export interface FourEyesInboxQuery {
  /** The market the per-market queues are read in; absent → they are not asked (`needs_market`). */
  readonly market?: string;
  readonly operatorId?: string;
  readonly grants: KnownGrants;
}

type Answer<T> = Result<T, ApiError> | undefined;

/**
 * Read every source the grants allow, in parallel, and fold each one into its count. A refused or failed read is `null`;
 * a source the grants cannot read is `null` too (and dropped by `buildFourEyesInbox`) and never asked for.
 */
export async function loadFourEyesInbox(runtime: FourEyesInboxRuntime, query: FourEyesInboxQuery): Promise<FourEyesCounts> {
  const { grants, operatorId } = query;
  const selfApproval = maySelfApprove(grants); // D868: a super administrator's own items are decidable too
  const market = query.market !== undefined && query.market.trim() !== '' ? query.market.trim() : undefined;
  const may = (key: FourEyesSourceKey): boolean => fourEyesReadAllowed(key, grants);
  const word = (permission: ScreenPolicy['permission']): boolean => grants === undefined || grantsAllow(grants, { permission, target: 'PLATFORM' });
  /**
   * The DRAFT packs of every country with a registered market, and (D420) the PENDING retirements of EVERY country — read
   * platform-wide (review W7G-01): a retirement is refused while its country has a live market, so its country usually
   * has no market at all and the catalog walk would never reach it. `null` when the catalog, any country or the pending
   * read failed.
   */
  const readPacks = async (): Promise<{ readonly packs: readonly (readonly CountryPackView[] | null)[]; readonly retirements: readonly CountryPackRetirementView[] } | null> => {
    const [catalog, pending] = await Promise.all([runtime.markets.listMarkets(), runtime.config.listPendingCountryPackRetirements()]);
    if (!catalog.ok || !pending.ok) return null; // unknown — the line is unavailable, never an empty (zero) read
    const perCountry = await Promise.all(fourEyesCountries(catalog.value.markets).map((cc) => runtime.config.listCountryPacks(cc)));
    return { packs: perCountry.map((r) => (r.ok ? r.value.packs : null)), retirements: pending.value.retirements };
  };
  const serviceAreas = runtime.ops?.serviceAreas?.bind(runtime.ops);
  const riderWalletInbox = runtime.riderWallet?.inbox?.bind(runtime.riderWallet);
  const [settings, payouts, handovers, refunds, packs, areas, riderWallet] = await Promise.all([
    may('settings_changes') ? runtime.settings.list() : undefined,
    may('payouts') && market !== undefined ? runtime.finance.payoutQueue({ market, status: 'REQUESTED', limit: FOUR_EYES_READ_LIMIT }) : undefined,
    may('cash_handovers') && market !== undefined ? runtime.cashHandovers.queue({ market, status: 'RECORDED', limit: FOUR_EYES_READ_LIMIT }) : undefined,
    may('refund_approvals') ? runtime.refundApprovals.list({ status: 'PENDING', limit: FOUR_EYES_READ_LIMIT }) : undefined,
    may('country_packs') ? readPacks() : undefined,
    may('service_areas') && market !== undefined && serviceAreas !== undefined ? serviceAreas(market) : undefined,
    may('rider_wallet') && market !== undefined && riderWalletInbox !== undefined ? riderWalletInbox(market, 'PENDING_APPROVAL') : undefined,
  ]);
  const fold = <T,>(res: Answer<T>, count: (v: T) => FourEyesCount): FourEyesRead => (res === undefined || !res.ok ? null : count(res.value));
  const perMarket = <T,>(key: FourEyesSourceKey, res: Answer<T>, count: (v: T) => FourEyesCount): FourEyesRead =>
    may(key) && market === undefined ? 'needs_market' : fold(res, count);
  return {
    settings_changes: fold(settings, (v) => countPendingSettings(v.settings, operatorId)),
    payouts: perMarket('payouts', payouts, (v) => countPendingPayouts(v.payouts, operatorId, word('payout:approve'), FOUR_EYES_READ_LIMIT, selfApproval)),
    cash_handovers: perMarket('cash_handovers', handovers, (v) => countRecordedHandovers(v.handovers, operatorId, word('payout:settle'), FOUR_EYES_READ_LIMIT, selfApproval)),
    refund_approvals: fold(refunds, (v) => countPendingRefunds(v.approvals, operatorId, FOUR_EYES_READ_LIMIT)),
    service_areas: serviceAreas === undefined ? 'absent' : perMarket('service_areas', areas, (v) => countSubmittedServiceArea(v.open, operatorId, grants === undefined || grantsAllow(grants, { permission: 'service_area:manage', target: 'GEO' }), selfApproval)),
    rider_wallet: riderWalletInbox === undefined ? 'absent' : perMarket('rider_wallet', riderWallet, (v) => countPendingRiderWallet(v.adjustments, operatorId, grants === undefined || grantsAllow(grants, { permission: 'rider_wallet:approve', target: 'GEO' }), FOUR_EYES_READ_LIMIT, selfApproval)),
    country_packs: packs === undefined || packs === null ? null : countDraftPacks(packs.packs, operatorId, word('config:publish'), packs.retirements, selfApproval),
  };
}
